import { useEffect, useState } from 'react';

import { Button } from '../../components/ui/Button';
import { brand } from '../../config/brand';

/**
 * The embed code, and the link that most people actually want.
 *
 * ## Two things, because people arrive wanting different ones
 *
 * Somebody writing a blog post wants an iframe. Somebody putting a track in a
 * Discord message or a Notion page wants the plain URL - and because oEmbed is
 * wired up, that URL becomes a player by itself in every consumer that asks.
 * Offering only the iframe would send the second person away with the wrong
 * answer.
 *
 * ## The size is a choice, not a setting page
 *
 * Two sizes: the ordinary one and a compact one for sidebars. Anything more is
 * a dialog with a slider in it, for a decision nobody wants to make twice.
 */
export function ShareEmbed({ trackId }: { trackId: number }) {
  const [copied, setCopied] = useState<'link' | 'code' | null>(null);
  const [compact, setCompact] = useState(false);

  // The tick clears itself; a button that says "copied" for ever is a button
  // that has stopped reporting anything.
  useEffect(() => {
    if (copied === null) return;
    const timer = setTimeout(() => setCopied(null), 2000);

    return () => clearTimeout(timer);
  }, [copied]);

  const link = `${brand.url}/tracks/${trackId}`;
  const width = compact ? 320 : 500;
  const height = compact ? 80 : 130;
  const code =
    `<iframe src="${brand.url}/embed/tracks/${trackId}" width="${width}" height="${height}" ` +
    `frameborder="0" scrolling="no" allow="autoplay"></iframe>`;

  const copy = async (value: string, which: 'link' | 'code') => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(which);
    } catch {
      // Clipboard denied - the textarea below is selectable, which is the
      // fallback that has always worked.
    }
  };

  return (
    <div className="flex flex-col gap-3" data-share-embed>
      <div className="flex flex-wrap items-center gap-2.5">
        <Button variant="secondary" data-copy-link onClick={() => copy(link, 'link')}>
          {copied === 'link' ? 'Link copied' : 'Copy link'}
        </Button>
        <Button variant="secondary" data-copy-embed onClick={() => copy(code, 'code')}>
          {copied === 'code' ? 'Embed copied' : 'Copy embed code'}
        </Button>

        <button
          type="button"
          aria-pressed={compact}
          data-embed-compact
          onClick={() => setCompact((current) => !current)}
          className={`meta-caps rounded-[var(--radius-pill)] border px-3 py-2 transition-colors ${
            compact
              ? 'border-transparent bg-accent text-accent-ink'
              : 'border-line text-soft hover:border-line-strong hover:text-ink'
          }`}
        >
          Compact
        </button>
      </div>

      <textarea
        readOnly
        value={code}
        data-embed-code
        onFocus={(event) => event.currentTarget.select()}
        className="min-h-16 resize-none rounded-[var(--radius-control)] border border-line bg-app px-3 py-2 font-mono text-[11px] leading-relaxed text-soft"
      />

      <p className="font-mono text-[10px] leading-relaxed text-ghost">
        pasting the plain link into Notion, Ghost, WordPress or Discord turns it into a player on
        its own
      </p>
    </div>
  );
}
